import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function SecretVancouverParks() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "secret-vancouver-parks-locals-love";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "bates-park": "Bates Park",
    "renfrew-ravine-park": "Renfrew Ravine Park",
    "charleson-park": "Charleson Park",
    "fraser-river-park": "Fraser River Park",
    "crab-park": "CRAB Park at Portside",
    "everett-crowley-park": "Everett Crowley Park",
    "habitat-island": "Habitat Island (Hinge Park)",
    "lighthouse-park": "Lighthouse Park",
    "iona-beach": "Iona Beach Regional Park",
    "pacific-spirit": "Pacific Spirit Regional Park",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Summaries of official or relevant references from the conversation.
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture.aspx",
      label: "City of Vancouver: Parks & Recreation",
    },
    {
      url: "https://metrovancouver.org/services/parks",
      label: "Metro Vancouver Regional Parks",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We match each id with the relevant section. We store only one coordinate set per park (approx. center or main entrance).
  const locations = [
    {
      id: "bates-park",
      lat: 49.2906,
      lng: -123.0123,
      popupText: "Bates Park: 669 Fellowes St, Vancouver, BC",
    },
    {
      id: "renfrew-ravine-park",
      lat: 49.2446,
      lng: -123.0455,
      popupText: "Renfrew Ravine Park: Near 29th Ave & Atlin St",
    },
    {
      id: "charleson-park",
      lat: 49.2665,
      lng: -123.1210,
      popupText: "Charleson Park: 999 Charleson Street, Vancouver, BC",
    },
    {
      id: "fraser-river-park",
      lat: 49.2076,
      lng: -123.1523,
      popupText: "Fraser River Park: 8705 Angus Drive, Vancouver, BC",
    },
    {
      id: "crab-park",
      lat: 49.2840,
      lng: -123.1033,
      popupText: "CRAB Park at Portside: 101 E Waterfront Rd",
    },
    {
      id: "everett-crowley-park",
      lat: 49.2113,
      lng: -123.0387,
      popupText: "Everett Crowley Park: 8200 Kerr Street area, Vancouver, BC",
    },
    {
      id: "habitat-island",
      lat: 49.2714,
      lng: -123.1105,
      popupText: "Habitat Island (Hinge Park): near 215 W 1st Ave",
    },
    {
      id: "lighthouse-park",
      lat: 49.3323,
      lng: -123.2633,
      popupText: "Lighthouse Park: 4902 Beacon Lane, West Vancouver, BC",
    },
    {
      id: "iona-beach",
      lat: 49.2170,
      lng: -123.2049,
      popupText: "Iona Beach Regional Park: Ferguson Rd, Richmond, BC",
    },
    {
      id: "pacific-spirit",
      lat: 49.2565,
      lng: -123.2188,
      popupText: "Pacific Spirit Regional Park: near UBC",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Secret Vancouver Parks Locals Love</title>
        <meta
          name="description"
          content="Discover Vancouver's lesser-known parks—wooded ravines, coastal hideaways, and quiet beaches. Includes addresses, tips, coordinates, and FAQs."
        />
        <meta
          name="keywords"
          content="Vancouver secret parks, hidden gems, local parks, nature, off-the-beaten-path"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/secret-vancouver-parks-locals-love.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Secret Vancouver Parks Locals Love"
        />
        <meta
          property="og:description"
          content="A practical guide to hidden parks in Vancouver, packed with tips, coordinates, and local secrets."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/secret-vancouver-parks-locals-love.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/secret-vancouver-parks-locals-love.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Secret Vancouver Parks Locals Love"
        />
        <meta
          name="twitter:description"
          content="Explore tranquil ravines, waterfront hideaways, and old-growth forests away from tourist crowds!"
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/secret-vancouver-parks-locals-love.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="A hidden pathway in a lush Vancouver park"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s reputation for stunning natural beauty often revolves
              around iconic spots like Stanley Park or the Seawall. But seasoned
              locals know there's a wealth of lesser-known green spaces just
              waiting to be explored. In this guide, we’ll reveal 10 secret
              parks across the city—many of them hidden ravines, secluded
              beaches, or tranquil forest pockets. Complete with practical
              tips, exact coordinates, and essential info on accessibility, 
              we hope this inspires you to venture off the well-trodden paths
              and experience Vancouver’s quieter corners. Ready to discover a 
              whole new side of the city?
            </p>

            {/* SECTION: Bates Park */}
            <h2 id="bates-park">Bates Park</h2>
            <p ref={(el) => setSpotRef("bates-park", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Bates Park</strong>{" "}
              <Marker
                coordY={49.2906}
                coordX={-123.0123}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Address: 669 Fellowes St, Vancouver, BC) is a tiny wooded haven
              tucked near the Second Narrows Bridge. Venture along informal
              trails to find quirky, homemade treehouses nestled among the
              foliage, offering glimpses of Burrard Inlet. You’ll forget you’re
              still in the city as you wander through this semi-wild slope.
              There are no washrooms or paved paths, so wear sturdy shoes.
              Parking is limited to street spots nearby, and transit access can
              be tricky. Bring a sense of adventure—and perhaps a friend—if you
              want a peaceful escape off the grid.
            </p>

            {/* SECTION: Renfrew Ravine Park */}
            <h2 id="renfrew-ravine-park">Renfrew Ravine Park</h2>
            <p ref={(el) => setSpotRef("renfrew-ravine-park", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Renfrew Ravine Park</strong>{" "}
              <Marker
                coordY={49.2446}
                coordX={-123.0455}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —found near 29th Avenue & Atlin Street—offers a rare slice of
              wilderness in East Vancouver. Still Creek flows openly through a
              forested ravine here, traversable via boardwalks and footbridges.
              Fall foliage is spectacular, and during autumn you might even spot
              salmon returning upstream. The park is partially accessible; 
              from the north entrance, find gentler, paved paths and a labyrinth
              in the adjoining community park. However, certain trails include
              stairs and dirt paths. Consider stopping by the neighborhood’s
              Moon Festival event in late September, when lanterns light up the
              ravine after dark.
            </p>

            {/* SECTION: Charleson Park */}
            <h2 id="charleson-park">Charleson Park</h2>
            <p ref={(el) => setSpotRef("charleson-park", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Charleson Park</strong>{" "}
              <Marker
                coordY={49.2665}
                coordX={-123.1210}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              on the south shore of False Creek (999 Charleson Street) 
              is a centrally located yet tranquil spot. Its manicured lagoon,
              cascading waterfall, and lush landscaping make it ideal for
              picnics, bird-watching, and scenic city views. Photographers love
              snapping skyline reflections in the pond. There’s also an
              off-leash dog area on the upper slope and playgrounds for kids.
              Access is relatively easy with nearby SkyTrain or bus routes, and
              the Seawall is just a short connection away—perfect for a post-park
              stroll.
            </p>

            {/* SECTION: Fraser River Park */}
            <h2 id="fraser-river-park">Fraser River Park</h2>
            <p ref={(el) => setSpotRef("fraser-river-park", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Fraser River Park</strong>{" "}
              <Marker
                coordY={49.2076}
                coordX={-123.1523}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (8705 Angus Drive) offers a tranquil riverfront 
              setting with sandy shores and tidal marshes. Watch tugboats pass
              on the Fraser, or explore the boardwalks that wind through
              marshy grasslands. Limited transit service means driving or
              biking is best. There’s ample free parking, a picnic shelter, and
              an off-leash dog zone (with some seasonal restrictions). Check
              tide tables if you want to walk out on the sand at low tide, and
              bring binoculars—waterfowl and eagles are common sights here.
            </p>

            {/* SECTION: CRAB Park */}
            <h2 id="crab-park">CRAB Park at Portside</h2>
            <p ref={(el) => setSpotRef("crab-park", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>CRAB Park at Portside</strong>{" "}
              <Marker
                coordY={49.2840}
                coordX={-123.1033}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , at 101 E Waterfront Road, is a hidden harborside 
              gem. Enjoy a small sandy beach, dog off-leash area, playground,
              and sweeping views of the working port and North Shore mountains.
              It’s a short walk from Waterfront Station, though you’ll likely
              use the pedestrian overpass at Main Street. There are memorials,
              a spray park for kids, and opportunities for prime ship-spotting.
              Swimming isn’t recommended, but splashing along the shore is
              common. Most visitors appreciate this quieter alternative to
              busier waterfront spots near Gastown.
            </p>

            {/* SECTION: Everett Crowley Park */}
            <h2 id="everett-crowley-park">Everett Crowley Park</h2>
            <p ref={(el) => setSpotRef("everett-crowley-park", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Everett Crowley Park</strong>{" "}
              <Marker
                coordY={49.2113}
                coordX={-123.0387}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (8200 Kerr Street area) is Vancouver’s fifth-largest park, with
              a fascinating backstory as a former landfill turned
              reforested haven. Now, it’s a dog-friendly off-leash haven with
              dirt trails winding through meadow and second-growth forest.
              Birdwatchers and casual hikers alike can enjoy the wetlands and
              slopes, though there are no formal facilities or accessible
              trails. Parking is free near Champlain Heights Community Centre.
              A highlight: peek-a-boo views of the Fraser River from certain
              ridges. If you’re interested in community-led reforestation, you
              might join an Earth Day tree-planting event here.
            </p>

            {/* SECTION: Habitat Island */}
            <h2 id="habitat-island">Habitat Island (Hinge Park)</h2>
            <p ref={(el) => setSpotRef("habitat-island", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Habitat Island</strong>{" "}
              <Marker
                coordY={49.2714}
                coordX={-123.1105}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , adjacent to 215 West 1st Avenue in 
              Southeast False Creek, is an artificial island built for
              ecological restoration. Often called “Beer Island” by locals,
              it’s reachable by a gravel path at low tide. Birds and marine
              life thrive here among native plants, driftwood, and intertidal
              boulders. Dogs are not allowed to protect wildlife. Hinge Park
              itself, on the mainland, includes wetlands, boardwalks, and 
              playful public art. It’s easily accessible via Olympic Village
              Station (Canada Line). Bring snacks, but note there are no
              washrooms on-site—plenty of cafés and facilities in Olympic
              Village are close by.
            </p>

            {/* SECTION: Lighthouse Park */}
            <h2 id="lighthouse-park">Lighthouse Park</h2>
            <p ref={(el) => setSpotRef("lighthouse-park", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Lighthouse Park</strong>{" "}
              <Marker
                coordY={49.3323}
                coordX={-123.2633}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (4902 Beacon Lane, West Vancouver) might be outside the city
              proper, but locals consider it an essential day trip. It protects
              a stand of old-growth Douglas fir and cedar, with well-marked
              forest trails leading to spectacular ocean viewpoints. The 
              historic lighthouse is visible from designated lookouts (though
              fenced off), and the rocky shoreline offers prime picnic spots and
              seal-watching. Trails vary in difficulty but most are manageable
              for casual hikers. There is no public transit directly inside, but
              the #250 bus from downtown Vancouver can drop you nearby at Marine
              Drive & Beacon Lane.
            </p>

            {/* SECTION: Iona Beach */}
            <h2 id="iona-beach">Iona Beach Regional Park</h2>
            <p ref={(el) => setSpotRef("iona-beach", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Iona Beach Regional Park</strong>{" "}
              <Marker
                coordY={49.2170}
                coordX={-123.2049}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Ferguson Rd, Richmond), minutes from Vancouver’s airport, 
              feels like another world. The broad sandy shoreline and a 4km
              jetty extending into the ocean offer unparalleled views of
              ocean, mountains, and passing aircraft. Birdwatchers adore the
              marshy wetlands—this area is an Important Bird Area with all sorts
              of ducks, shorebirds, and raptors. There’s free parking but no
              transit access, so plan to drive or cycle. Bring water, snacks,
              sun protection, and be prepared for wind on the jetty.
            </p>

            {/* SECTION: Pacific Spirit Regional Park */}
            <h2 id="pacific-spirit">Pacific Spirit Regional Park</h2>
            <p ref={(el) => setSpotRef("pacific-spirit", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Pacific Spirit Regional Park</strong>{" "}
              <Marker
                coordY={49.2565}
                coordX={-123.2188}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              near UBC offers over 55 kilometers of forested trails, 
              beach access points, and one of the region’s best examples of
              a coastal temperate rainforest. Camosun Bog, Wreck Beach’s
              clothing-optional shore, and dog-friendly off-leash trails make
              this park highly diverse. Trails are mostly dirt or gravel, so
              wear sturdy shoes. Public transit is excellent around UBC,
              but exact access points vary. If you want a quick forest-bathing
              session or a day-long hike, Pacific Spirit accommodates all kinds
              of adventures.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are these secret parks free to visit?</h3>
              <p>
                Yes. All listed parks offer free entry, though some 
                may have limited or pay parking nearby (e.g., downtown near CRAB
                Park). Check signage wherever you choose to park your car. 
                Otherwise, enjoy these beautiful green spaces at no cost!
              </p>

              <h3>2. Can I bring my dog to these parks?</h3>
              <p>
                In most cases, yes—Vancouver is fairly dog-friendly. Some 
                parks (like Fraser River Park, Everett Crowley Park, Charleson 
                Park, or CRAB Park) have designated off-leash zones. Others 
                (Habitat Island) do not allow dogs, or require leashes. Always
                check posted signs and please pick up after your pet.
              </p>

              <h3>3. Is it safe to visit these lesser-known parks alone?</h3>
              <p>
                Vancouver’s parks are generally safe, but exercising standard
                caution is wise—especially in more remote areas. Visit in
                daylight, tell someone your plans if heading off to secluded
                trails, and keep valuables out of sight in your vehicle.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              <strong>Hours:</strong> Most Vancouver parks open around 6am and
              close by 10pm. Observe posted signs for specific hours.
            </p>
            <p>
              <strong>Leave No Trace:</strong> Pack out your garbage, respect
              sensitive habitats, and avoid disturbing wildlife. Stay on
              designated trails to protect fragile ecosystems.
            </p>
            <p>
              <strong>Dogs:</strong> Off-leash rules apply only in designated
              areas; otherwise, keep dogs leashed. Pay attention to seasonal
              changes (e.g., certain beaches switch between on-leash and
              off-leash at different times).
            </p>
            <p>
              <strong>Smoking and Alcohol:</strong> Smoking is banned in all
              Vancouver parks. Alcohol consumption is prohibited unless
              officially designated (pilot programs for certain parks may 
              vary; check signage).
            </p>
            <p>
              <strong>Safety:</strong> Heed any notices about wildlife such as
              coyotes, and do not feed or approach any wild animals. Steep
              slopes, ravine edges, or rocky shorelines can be slippery—use
              caution and supervise children.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Beyond the bustling Seawall and famous Stanley Park lie these
              intimate green escapes that locals cherish. Whether you’re
              birdwatching along the Fraser, climbing hidden tree forts in
              Bates Park, or savoring city views from Charleson’s pond, each
              park offers a unique glimpse of Vancouver’s natural variety.
              Follow the regulations, respect the land, and you’ll uncover
              genuine serenity—even in the heart of a major city. Happy
              exploring!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}